import { NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import rffSubmitResponse from 'src/common/utils/rffSubmitResponse';
import type { History, Location } from 'history';
import type { AppDispatch } from 'src/common/hooks/useAppDispatch';
import { type TripAction } from 'src/inbox/type/TripAction';
import { type StateToActionMap, type TripStateState } from 'src/types/trips/Trips';
import {
  EXTERNAL_PATHS,
  PATHS,
  SUPPORT_URLS,
  USER_ROLES,
} from '../../../common/constants';
import {
  MESSAGE_BLACKLIST_STATES,
  PAGES,
  PREFERRED_DATE_FIELD,
  TRIP_STATES,
} from '../../constants';
import {
  postMessageCall,
} from '../../ducks/messages';
import {
  cancelRequestedChangesCall,
  cancelTripCall,
  cancelOfferCall,
  cancelReservationCall,
  archiveTripCall,
  unarchiveTripCall,
  declineInquiryCall,
} from '../../ducks/trips';
import {
  focusCalendar,
} from '../../../calendar/ducks/calendar';
import { getUserRole, firstTripRedirectThunk, isChargeEligible } from '../../helpers';
import ExternalLink from '../../../common/components/ExternalLink';
import TripMenuButton from '../TripMenuButton';
import { isWithinCalendarConstraints } from '../../../calendar/hooks/useCalendar';

const CONTACT_SUPPORT: TripAction = {
  name: 'contact support',
  component: ExternalLink,
  icon: 'question',
  title: 'Contact Support',
  url: SUPPORT_URLS.REQUEST,
};

export const SEND_MESSAGE_ACTION_NAME = 'send message';
const SEND_MESSAGE: TripAction = {
  name: SEND_MESSAGE_ACTION_NAME,
  icon: 'paper-plane',
  title: 'Send a Message',
  destination: `${PAGES.MESSAGES}/`,
};

const SEND_MESSAGE_MODAL: TripAction = {
  ...SEND_MESSAGE,
  submitText: 'Send Message',
  component: TripMenuButton,
  longDescription: 'You can request or provide more information before sending your booking offer to the renter.',
  placeholder: 'Your message...',
  call: postMessageCall,
};

const SEND_MESSAGE_LINK: TripAction = {
  ...SEND_MESSAGE,
  component: NavLink,
};

const EDIT_OFFER: TripAction = {
  name: 'edit offer',
  component: NavLink,
  destination: `${PAGES.EDIT}/`,
  icon: 'pencil',
  title: 'Edit Offer',
  shortDescription: 'This will cancel and resend',
};

const OPEN_IN_CALENDAR: TripAction = {
  name: 'open in calendar',
  component: NavLink,
  url: PATHS.CALENDAR,
  icon: 'calendar-open',
  title: 'Open in Calendar',
  onClick: (dispatch: AppDispatch, trip: ImmutableTrip) => {
    dispatch(focusCalendar({
      selectedDate: trip.get(PREFERRED_DATE_FIELD),
      focusedThreadId: trip.get('pk'),
      scrollToEvent: true,
    }));
  },
  isDisabled: (trip: ImmutableTrip) => {
    const tripStartDate = trip.get(PREFERRED_DATE_FIELD);
    return !isWithinCalendarConstraints(tripStartDate);
  },
};

const CHARGE: TripAction = {
  name: 'charge',
  component: TripMenuButton,
  icon: 'credit-card-charge',
  title: 'Charge Add-On',
  onClick: ((_dispatch: Dispatch, trip: ImmutableTrip) => {
    const tripId = trip.get('pk').toString();
    const searchParams = new URLSearchParams({ trip: tripId });
    const url = `${EXTERNAL_PATHS.CHARGE_ADD_ON}price/?${searchParams.toString()}`;
    // Redirect to the price screen of the charge add-on journey
    window.location.href = url;
  }),
  isDisabled: (trip: ImmutableTrip) => !isChargeEligible(trip),
};

const CANCEL_OFFER_ACTION_NAME = 'cancel offer';
const CANCEL_OFFER: TripAction = {
  name: CANCEL_OFFER_ACTION_NAME,
  component: TripMenuButton,
  icon: 'minus-circle',
  title: 'Cancel Offer',
  longDescription: 'Are you sure you want to cancel your offer to this customer?',
  cancelText: "Don't Cancel",
  call: cancelOfferCall,
  dropdownFieldName: 'cancel_reason',
  dropdownPlaceholder: 'Cancellation Reason',
  messageLabel: 'Cancellation Details*',
  placeholder: 'Message for your customer about why you have to cancel offer.',
  withMessageCharacterCounter: true,
};

export const CANCEL_INQUIRY: TripAction = {
  name: 'cancel inquiry',
  component: TripMenuButton,
  icon: 'minus-circle',
  title: 'Cancel Inquiry',
  longDescription: 'Are you sure you want to cancel your inquiry?',
  cancelText: "Don't Cancel",
  call: cancelTripCall,
};

export const CANCEL_REQUESTED_CHANGES: TripAction = {
  name: 'Cancel Requested Changes',
  component: TripMenuButton,
  icon: 'note-cancel',
  title: 'Cancel Requested Changes',
  longDescription: 'Are you sure you want to cancel the changes you requested to this offer?',
  submitText: 'Cancel Changes',
  cancelText: "Don't Cancel",
  call: cancelRequestedChangesCall,
  withMessageField: false,
};

export const DECLINE_INQUIRY_ACTION_NAME = 'decline inquiry';
const DECLINE_INQUIRY: TripAction = {
  component: TripMenuButton,
  icon: 'x-circle',
  name: DECLINE_INQUIRY_ACTION_NAME,
  title: 'Decline Inquiry',
  cancelText: 'Cancel',
  call: declineInquiryCall,
  dropdownFieldName: 'cancel_reason',
  dropdownPlaceholder: 'Cancellation Reason',
  longDescription: 'Your customer will be advised that their inquiry was declined and your availability calendar will be updated.',
  messageLabel: 'Cancellation Details*',
  withMessageCharacterCounter: true,
  placeholder: 'Message for your customer about why you have to decline their inquiry.',
};

export const CANCEL_RESERVATION_ACTION_NAME = 'cancel reservation';
const CANCEL_RESERVATION: TripAction = {
  name: CANCEL_RESERVATION_ACTION_NAME,
  component: TripMenuButton,
  icon: 'x-circle',
  title: 'Cancel Booking',
  longDescription: 'Are you sure you want to cancel this booking? Please note that service fees are non-refundable.',
  placeholder: 'Reason for cancellation',
  messageLabel: 'Cancellation Details*',
  withMessageCharacterCounter: true,
  cancelText: "Don't Cancel",
  submitText: 'Cancel Trip',
  call: cancelReservationCall,
  dropdownFieldName: 'cancel_reason',
  dropdownPlaceholder: 'Cancellation Reason',
};

export const ARCHIVE_TRIP: TripAction = {
  name: 'archive',
  component: TripMenuButton,
  icon: 'archive-box',
  title: 'Archive this inquiry',
  onClick: (
    dispatch: AppDispatch,
    trip: ImmutableTrip,
    options?: { history: History; location: Location },
  ) => {
    if (options) {
      const { history, location } = options;
      dispatch(archiveTripCall(trip.get('pk')))
        .then(() => dispatch(firstTripRedirectThunk(history, location)))
        .catch(rffSubmitResponse());
    }
  },
};

export const UNARCHIVE_TRIP: TripAction = {
  name: 'unarchive',
  component: TripMenuButton,
  icon: 'archive-box',
  title: 'Unarchive this inquiry',
  onClick: (
    dispatch: AppDispatch,
    trip: ImmutableTrip,
    options?: { history: History; location: Location },
  ) => {
    if (options) {
      const { history, location } = options;
      dispatch(unarchiveTripCall(trip.get('pk')))
        .then(() => dispatch(firstTripRedirectThunk(history, location)))
        .catch(rffSubmitResponse());
    }
  },
};

const getArchiveAction = (archived: boolean) => (
  archived
    ? UNARCHIVE_TRIP
    : ARCHIVE_TRIP
);

const getSendMessageAction = (tripState: TripStateState) => (
  MESSAGE_BLACKLIST_STATES.includes(tripState)
    ? SEND_MESSAGE_MODAL
    : SEND_MESSAGE_LINK
);

const getRenterActions = (trip: ImmutableTrip) => [
  getSendMessageAction(trip.getIn(['state', 'state'])),
  CANCEL_REQUESTED_CHANGES,
  CANCEL_INQUIRY,
  CANCEL_RESERVATION,
  getArchiveAction(trip.get('archived', false)),
  CONTACT_SUPPORT,
];

const getOwnerActions = (trip: ImmutableTrip): TripAction[] => [
  CHARGE,
  getSendMessageAction(trip.getIn(['state', 'state'])),
  OPEN_IN_CALENDAR,
  EDIT_OFFER,
  CANCEL_OFFER,
  DECLINE_INQUIRY,
  CANCEL_RESERVATION,
  getArchiveAction(trip.get('archived', false)),
  CONTACT_SUPPORT,
];

const COMMON_ACTIONS = [
  SEND_MESSAGE,
  ARCHIVE_TRIP,
  UNARCHIVE_TRIP,
  CONTACT_SUPPORT,
];

/**
 * Maps trip states to their available actions for owners and renters.
 * This determines which actions are available to users based on the current state of a trip.
 */
export const STATE_TO_ACTIONS_MAP: StateToActionMap = {
  [TRIP_STATES.OWNER_DIRECT_INQUIRY]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_MATCHING_INQUIRY]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_OFFER_SENT]: [
    EDIT_OFFER,
    CANCEL_OFFER,
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_CHANGES_REQUESTED]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_OFFER_CANCELLED]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_OFFER_AUTO_CANCELLED]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_OFFER_EDITED]: [
    EDIT_OFFER,
    CANCEL_OFFER,
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_OFFER_EXPIRED]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_CUSTOM_OFFER_EXPIRED]: [
    DECLINE_INQUIRY,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_RESERVATION_CONFIRMED]: [
    CHARGE,
    CANCEL_RESERVATION,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_RESERVATION_CANCELLED_BY_RENTER]: [
    DECLINE_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_RESERVATION_CANCELLED_BY_OWNER]: [
    DECLINE_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_TRIP_COMPLETE_REVIEW_PENDING]: [
    CHARGE,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_TRIP_COMPLETE_REVIEW_WAITING]: [
    CHARGE,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.OWNER_TRIP_COMPLETE_REVIEW_DONE]: [
    CHARGE,
    OPEN_IN_CALENDAR,
    ...COMMON_ACTIONS,
  ],

  [TRIP_STATES.RENTER_DIRECT_INQUIRY]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_MATCHING_INQUIRY]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_OFFER_RECEIVED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_CHANGES_REQUESTED]: [
    CANCEL_REQUESTED_CHANGES,
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_OFFER_CANCELLED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_OFFER_EDITED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_OFFER_EXPIRED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_CUSTOM_OFFER_EXPIRED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_INQUIRY_DECLINED]: [
    CANCEL_INQUIRY,
    ...COMMON_ACTIONS,
  ],
  [TRIP_STATES.RENTER_INQUIRY_CANCELLED]: [
    ARCHIVE_TRIP,
    UNARCHIVE_TRIP,
    CONTACT_SUPPORT,
  ],
  [TRIP_STATES.RENTER_RESERVATION_CONFIRMED]: [
    CANCEL_RESERVATION,
    ...COMMON_ACTIONS,
  ],
};

export const CANCEL_ACTIONS = [
  CANCEL_OFFER_ACTION_NAME,
  CANCEL_RESERVATION_ACTION_NAME,
  DECLINE_INQUIRY_ACTION_NAME,
];

export const getRoleSpecificActions = (
  trip: ImmutableTrip,
): TripAction[] => (
  getUserRole(trip) === USER_ROLES.OWNER
    ? getOwnerActions(trip)
    : getRenterActions(trip)
);

export const getActions = (state: TripStateState) => (
  STATE_TO_ACTIONS_MAP[state] || COMMON_ACTIONS
);
